import { wistiaPlayerStyle } from "./source.js";
import { normalizeWistiaPlayer } from "./normalize.js";
import { wistiaMediaOptions } from "./options.js";
import { WistiaPlayer } from "@wistia/wistia-player";
//#region src/adapter.ts
/**
* The tag Wistia registers its player element under, and it has to be declared here: this is the module whose
* evaluation registers the element and the only thing React takes from it, so naming it somewhere that pulls none of
* Wistia in would leave a bundler free to drop this module and React rendering an undefined tag.
*
* `server.ts` declares it too, for the runtimes that resolve there instead.
*
* @internal
*/
const WISTIA_PLAYER_TAG = "wistia-player";
/**
* Wistia is the one media here that ships a web component of its own, so this is that component rather than a wrapper
* around one; the platform packages give it a tag and a player store.
*
* `normalizeWistiaPlayer` gives the player the members and events `HTMLMediaElement` has that Wistia names differently
* or not at all. This class does the rest: the attributes a media element is written with, which Wistia has its own
* names, spellings, and defaults for.
*/
var WistiaAdapter = class extends WistiaPlayer {
	static {
		this.defaultProps = {
			src: "",
			autoplay: false,
			defaultMuted: false,
			muted: false,
			loop: false,
			controls: false,
			playsInline: true,
			preload: "metadata",
			poster: "",
			source: null
		};
	}
	static get observedAttributes() {
		return [...WistiaPlayer.observedAttributes, ...Object.keys(MEDIA_ATTRIBUTES)];
	}
	constructor() {
		super();
		normalizeWistiaPlayer(this);
	}
	connectedCallback() {
		super.connectedCallback?.();
		applyOptions(this);
	}
	attributeChangedCallback(name, oldValue, newValue) {
		const apply = MEDIA_ATTRIBUTES[name];
		if (!apply) {
			super.attributeChangedCallback(name, oldValue, newValue);
			return;
		}
		if (oldValue !== newValue) apply(this, newValue);
	}
};
/**
* What each attribute a media element is written with does to a Wistia player.
*
* The five that configure the player go through `applyOptions` together, which is what lets `controls` going away put
* the player back the way this media would have had it rather than the way Wistia would. Wistia observes `autoplay`,
* `poster`, and `preload` too, and these readings win. The other three mean something on their own: Wistia has no `src`
* and names its media by id, `muted` is the state to start in rather than the live one a mute button drives, and
* `playsinline` goes no further since Wistia plays inline anyway.
*/
const MEDIA_ATTRIBUTES = {
	autoplay: applyOptions,
	controls: applyOptions,
	loop: applyOptions,
	poster: applyOptions,
	preload: applyOptions,
	muted: (player, value) => {
		player.defaultMuted = value !== null;
	},
	playsinline: (player, value) => {
		player.playsInline = value !== null;
	},
	src: (player, value) => {
		player.src = value ?? "";
	}
};
/** The player's whole configuration, worked out from scratch: cheaper than keeping it up to date piecemeal. */
function applyOptions(player) {
	const controls = player.hasAttribute("controls");
	Object.assign(player, wistiaMediaOptions({
		autoplay: player.hasAttribute("autoplay"),
		controls,
		loop: player.hasAttribute("loop"),
		poster: player.getAttribute("poster") ?? void 0,
		preload: player.getAttribute("preload") ?? void 0
	}));
	if (player.source) Object.assign(player, player.source);
	Object.assign(player.style, wistiaPlayerStyle(controls));
}
//#endregion
export { WISTIA_PLAYER_TAG, WistiaAdapter };
